import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

/**
 * Read-side structural subset of the wire's PresentationContentOutput (this package deliberately
 * has no @air/api dependency). Only the block envelope is modeled: id keys the block, hidden?
 * skips it, type routes it to a renderer, and array order is render order. Per-block payload
 * fields get modeled as their renderers land; wire blocks carry extra fields, which structural
 * typing tolerates.
 *
 * One shape serves both authored bodies on a collection link — the root page's
 * `presentationContent` and each collection sub-page's own `content` — because the wire types them
 * identically and the same renderer draws both. `type: 'title'` has no extra payload — it
 * displays the current page name at render time.
 */
type ShortIdPresentationContent = {
  blocks: Array<{
    id: string;
    hidden?: boolean;
    type: 'text' | 'title' | 'image' | 'video' | 'links' | 'assets' | 'files' | 'boards';
    // Links-block payload (type: 'links'), server-resolved OUTPUT shape. Optional because the
    // envelope models every block variant with one structural shape — the wire guarantees all
    // four fields on links blocks. `thumbnail` is resolved server-side (`url` is ready to
    // render); the assetId/versionId refs are modeled so wire-shaped read values stay
    // assignable. None of these field names collide with other block variants' payloads.
    style?: 'pill' | 'card';
    alignment?: 'left' | 'center' | 'right';
    size?: 'small' | 'medium' | 'large';
    links?: Array<{
      id: string;
      label: string;
      url: string;
      thumbnail?: {
        url: string;
        assetId: string;
        versionId: string;
      };
    }>;
    // Image/video-block payload (types 'image' and 'video'), server-resolved OUTPUT shape.
    // Optional for the same structural-envelope reason — the wire guarantees assetId/versionId
    // (and a resolved, ready-to-render `url`) on both block types. `loop`/`autoplay`/`controls`/
    // `muted` are video-only playback toggles; absent values get frontend defaults in the
    // renderer (autoplay/loop/muted on, controls off). The asset refs are modeled so wire-shaped
    // read values stay assignable; public rendering only consumes `url`.
    assetId?: string;
    versionId?: string;
    url?: string;
    extendToEdges?: boolean;
    roundCorners?: boolean;
    loop?: boolean;
    autoplay?: boolean;
    controls?: boolean;
    muted?: boolean;
    // Text-block payload (type: 'text'). `body` is a JSON-stringified ProseMirror document
    // (the wire types it `string`); `unknown` because the envelope is defensive — renderers
    // parse and structurally validate through parsePresentationTextBlockBody before rendering.
    body?: unknown;
    inset?: boolean;
  }>;
};

/** Discriminator for the share-link object mounted under ShortIdProvider. */
export type ShortIdObjectType = 'clip' | 'asset' | 'board' | 'collection' | 'canvas';

type ShortIdProviderContextValue = {
  shortId: string;
  objectType: ShortIdObjectType;
  objectId: string;
  /**
   * From `getByShortId.isOnPaidPlan` on public viewers (and the owner's subscription on
   * editor/preview). Absent is designed: older payloads and tests that do not care about plan.
   */
  isOnPaidPlan?: boolean;
  presentationSettings?: {
    // Forces the public page's theme: 'dark'/'light' override the visitor's preference; absent
    // (legacy links, and drafts that omit the key because Light is the default) forces Light on
    // surfaces that own the document, so workspace/OS theme cannot leak in. 'toggle' (a future
    // viewer-choice mode) still leaves the visitor's preference untouched.
    colorMode?: 'light' | 'dark' | 'toggle';
    // Fades the cover image's bottom edge into the page background; absent (links saved before
    // the field existed) renders no fade.
    coverFade?: boolean;
    // Read-side coverImage subset consumed by public renderers; region values are percent units.
    coverImage?: {
      region: { top: number; left?: number; width?: number; height?: number };
      url: string;
    };
    // How the cover band insets/spans; absent (links saved before the field existed) renders as `fullWidth`.
    coverWidth?: 'inset' | 'fullWidth' | 'immersive';
    // Content-column max width; absent (links saved before the field existed) renders as `wide`.
    pageWidth?: 'narrow' | 'standard' | 'wide';
    customColors?: {
      headerBgColor?: { hex: string };
      buttonBgColor?: { hex: string };
      headlineBgColor?: { hex: string };
      pageBgColor?: { hex: string };
      accentBgColor?: { hex: string };
    };
    // Read-side customFonts subset (server-resolved OUTPUT shape). Only `headlineFont` renders —
    // applied to headline elements (page title, gallery board titles) on public collection pages;
    // `bodyFont` exists on the wire but nothing writes or renders it, so it isn't modeled. `url`
    // is an uploaded font file the page must register via @font-face: always present for `asset`,
    // present on `contextSource` only when the brand-kit value resolved to an upload rather than
    // a built-in family. The wire's `contextSource` refs carry the full brand-kit id triple
    // (source/field/value); public rendering only resolves fonts by family/url, but the ids are
    // modeled so wire-shaped read values stay assignable.
    customFonts?: {
      headlineFont?:
        | { type: 'system'; fontFamily: string; variant?: string }
        | { type: 'asset'; assetId: string; versionId: string; fontFamily: string; variant?: string; url: string }
        | {
            type: 'contextSource';
            contextSourceId: string;
            contextSourceFieldId: string;
            contextSourceFieldValueId: string;
            fontFamily: string;
            variant?: string;
            url?: string;
          };
    };
    customLogo?: {
      url: string;
    };
    hideAirLogo?: boolean;
    hideDownloadAll?: boolean;
    hideInfoTab?: boolean;
    hideSearchBar?: boolean;
    lockedControls?: {
      viewType?: boolean;
      cardSize?: boolean;
      thumbnailStyle?: boolean;
      metadataFieldsVisible?: boolean;
      customFieldsVisible?: boolean;
      sortOrder?: boolean;
      expandNested?: boolean;
    };
    // Corner rounding for the page chrome and gallery cards; absent (links saved before the field
    // existed) renders as `standard` — the original radii.
    thumbnailRadius?: 'standard' | 'subtle' | 'square';
  };
  // The link's ROOT page body. Absent → the link has no authored content and public pages render
  // the legacy layout; { blocks: [] } is a deliberately-empty page. Sub-pages carry their own
  // `content` below rather than overriding this one.
  presentationContent?: ShortIdPresentationContent;
  // Read-side structural subset of the wire's PresentationPagesOutput (this package deliberately has
  // no @air/api dependency). A FLAT list of the link's sub-pages — `parentId` is what gives it a tree
  // shape, so nav renderers build the hierarchy themselves rather than walking nested children.
  // Only the fields navigation needs are modeled: `type` discriminates the two variants, collection
  // pages carry the `collectionId` a nav entry routes to plus the `name` it renders, a `draft`
  // flag that hides unpublished pages from viewers, and the `content` blocks the sub-page renderer
  // draws when a viewer is on that page (required on the wire — every collection sub-page has a
  // body, even an empty one). A collection sub-page can also carry a per-page cover override in
  // `settings.coverImage` — the ONE per-page design override that exists (fonts/colors/logo stay
  // share-link-wide). It reuses the root cover read-side shape (region + resolved url); INDEPENDENT
  // of the root, so an absent override renders NO cover on that page rather than falling back.
  // Modeled incrementally, same rule as the per-block payload fields above — wire values carry
  // extra fields, which structural typing tolerates. Absent → the link has no authored sub-pages;
  // { subPages: [] } is a deliberately-empty set. Both render as no navigation.
  presentationPages?: {
    // Suppresses the navigation chrome while keeping the authored pages reachable; absent (links
    // saved before the field existed) renders navigation.
    hideNavigation?: boolean;
    subPages: Array<
      | {
          type: 'collection';
          collectionId: string;
          name: string;
          parentId: string;
          draft?: boolean;
          content: ShortIdPresentationContent;
          // Per-page cover override, INDEPENDENT of the root cover — set → this page shows its own;
          // absent (or an explicit null the wire uses to represent a cleared override) → this page
          // shows no cover (no inheritance from the root). Reuses the root coverImage read-side
          // subset (region percent units + resolved url).
          settings?: {
            coverImage?: {
              region: { top: number; left?: number; width?: number; height?: number };
              url: string;
            } | null;
          };
        }
      | {
          type: 'link';
          label: string;
          url: string;
          parentId: string;
        }
    >;
  };
};

const ShortIdProviderContext = createContext<ShortIdProviderContextValue | undefined>(undefined);

export type ShortIdProviderProps = ShortIdProviderContextValue & {
  children: ReactNode;
};

export const ShortIdProvider = memo(
  ({
    children,
    shortId,
    objectType,
    objectId,
    isOnPaidPlan,
    presentationContent,
    presentationPages,
    presentationSettings,
  }: ShortIdProviderProps) => {
    const value = useMemo(
      () => ({
        isOnPaidPlan,
        objectId,
        objectType,
        presentationContent,
        presentationPages,
        presentationSettings,
        shortId,
      }),
      [isOnPaidPlan, objectId, objectType, presentationContent, presentationPages, presentationSettings, shortId],
    );

    return <ShortIdProviderContext.Provider value={value}>{children}</ShortIdProviderContext.Provider>;
  },
);

ShortIdProvider.displayName = 'ShortIdProvider';

export const useShortIdContext = () => {
  const context = useContext(ShortIdProviderContext);

  if (!context) {
    throw 'ShortIdProviderContext used outside of ShortIdProvider';
  }

  return context;
};
